import { getAnalyzedCandles } from "./structureEngine";

export const DEFAULT_TREND_LINE_SETTINGS = {
  trendLineEnabled: true,
  trendLineColor: "#FFFFFF",
  trendLineGlowColor: "#00FFFF",
  trendLineWidth: 2,
  trendLineGlowWidth: 5,
  minimumSwings: 5,
  maximumSwings: 10,
  pivotStrength: 5,
  minimumSwingDistanceMultiplier: 1.25,
};

function averageRange(candles) {
  const sample = candles.slice(-100);
  return sample.reduce((total, candle) => total + candle.high - candle.low, 0) /
    Math.max(sample.length, 1);
}

/** Only pivots with enough candles on both sides are emitted. */
export function detectConfirmedPivots(candles, settings = {}) {
  const options = { ...DEFAULT_TREND_LINE_SETTINGS, ...settings };
  const closedCandles = getAnalyzedCandles(candles);
  const strength = Math.max(1, Number(options.pivotStrength) || 5);
  const minimumDistance = averageRange(closedCandles) * options.minimumSwingDistanceMultiplier;
  const pivots = [];

  for (let index = strength; index < closedCandles.length - strength; index += 1) {
    const candle = closedCandles[index];
    let high = true;
    let low = true;

    for (let offset = 1; offset <= strength; offset += 1) {
      high &&= candle.high > closedCandles[index - offset].high && candle.high > closedCandles[index + offset].high;
      low &&= candle.low < closedCandles[index - offset].low && candle.low < closedCandles[index + offset].low;
    }

    if (high) pivots.push({ type: "HIGH", time: candle.time, price: candle.high, index, confirmedIndex: index + strength });
    if (low) pivots.push({ type: "LOW", time: candle.time, price: candle.low, index, confirmedIndex: index + strength });
  }

  return pivots.reduce((valid, pivot) => {
    const previous = valid[valid.length - 1];
    if (!previous || previous.type !== pivot.type || Math.abs(pivot.price - previous.price) >= minimumDistance) {
      valid.push(pivot);
    } else if ((pivot.type === "HIGH" && pivot.price > previous.price) || (pivot.type === "LOW" && pivot.price < previous.price)) {
      valid[valid.length - 1] = pivot;
    }
    return valid;
  }, []);
}

function latestDirectionalRun(pivots, type, isValidNext, maximumSwings) {
  const sameType = pivots.filter((pivot) => pivot.type === type);
  if (sameType.length < 2) return [];

  const run = [sameType[sameType.length - 1]];
  for (let index = sameType.length - 2; index >= 0 && run.length < maximumSwings; index -= 1) {
    const newer = run[0];
    const older = sameType[index];
    if (!isValidNext(older, newer)) break;
    run.unshift(older);
  }
  return run;
}

export function detectMarketTrend(candles, settings = {}) {
  const options = { ...DEFAULT_TREND_LINE_SETTINGS, ...settings };
  const pivots = detectConfirmedPivots(candles, options);
  const downSwings = latestDirectionalRun(pivots, "HIGH", (older, newer) => newer.price < older.price, options.maximumSwings);
  const upSwings = latestDirectionalRun(pivots, "LOW", (older, newer) => newer.price > older.price, options.maximumSwings);
  const minimum = Math.max(2, options.minimumSwings);

  if (downSwings.length < minimum && upSwings.length < minimum) return { direction: null, pivots, swings: [] };
  if (downSwings.length >= minimum && (upSwings.length < minimum || downSwings.at(-1).time > upSwings.at(-1).time)) {
    return { direction: "DOWN", pivots, swings: downSwings };
  }
  return { direction: "UP", pivots, swings: upSwings };
}
